import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { DeepPartial, Optional } from '@whistler-ui/k2/types';

import { PRODUCTION, TESTING } from '../../constants';
import {
  getEnvironments,
  getInboundCSR,
  getLatestMtlsVerification,
  postCSR,
  postMtlsVerification,
  putEnvironments,
} from '../../services';
import { useEntitlementsStore } from '../../store/entitlements';
import { Environment, isMtlsVerificationSettled } from '../../types';

const MTLS_POLL_INTERVAL_MS = 2000;

export const useGetInboundCSR = (environmentId: Optional<string>) => {
  return useQuery({
    queryKey: ['get_inbound_csr', environmentId],
    queryFn: () => getInboundCSR({ environmentId }),
    retry: 1,
    enabled: Boolean(environmentId),
    select: data => {
      if (data) {
        return data;
      }

      return {
        inbound_cert: null,
        inbound_cert_expiration_date: null,
      };
    },
  });
};

export const useCreateInboundCSR = (environmentId: Optional<string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_inbound_csr'],
    mutationFn: postCSR,
    onSuccess: data => {
      if (!data) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ['get_inbound_csr', environmentId],
      });

      queryClient.invalidateQueries({
        queryKey: ['get_environments'],
      });
    },
  });
};

export const useGetEnvironments = (): UseQueryResult<
  Environment | undefined
> => {
  const { isLiveView } = useEntitlementsStore();

  return useQuery({
    queryKey: ['get_environments', isLiveView],
    queryFn: getEnvironments,
    select: data => {
      return data.find((env: any) => {
        if (isLiveView) {
          return env.type === PRODUCTION;
        }

        return env.type === TESTING;
      });
    },
  });
};

// Full environment list, sharing the cache of `useGetEnvironments` above so no
// extra request is made. Used by the impersonation banner to read per-env
// maintenance flags.
export const useGetAllEnvironments = (
  enabled = true,
): UseQueryResult<Environment[]> => {
  const { isLiveView } = useEntitlementsStore();

  return useQuery({
    queryKey: ['get_environments', isLiveView],
    queryFn: getEnvironments,
    enabled,
  });
};

export const useStartMtlsVerification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['start_mtls_verification'],
    mutationFn: postMtlsVerification,
    onSuccess: (_, environmentId) => {
      // Refetch immediately so polling picks up the freshly created run.
      queryClient.invalidateQueries({
        queryKey: ['get_latest_mtls_verification', environmentId],
      });
    },
  });
};

/**
 * Latest mTLS verification of the environment. Loads on mount so the last
 * run persists in the UI, and polls while a run — the one created with
 * `verificationId`, or one still in flight — has not settled yet. `data`
 * stays null while a fresh run is pending, so consumers can read it as
 * "result ready".
 */
export const useGetLatestMtlsVerification = (
  environmentId: Optional<string>,
  verificationId: Optional<string>,
) => {
  return useQuery({
    queryKey: ['get_latest_mtls_verification', environmentId],
    queryFn: () => getLatestMtlsVerification(environmentId as string),
    enabled: Boolean(environmentId),
    retry: false,
    refetchOnMount: 'always',
    refetchInterval: query => {
      const verification = query.state.data;

      if (!verification) {
        // Nothing recorded yet: poll only while waiting for a run we started.
        return verificationId ? MTLS_POLL_INTERVAL_MS : undefined;
      }

      const awaitingRun =
        Boolean(verificationId) && verification.id !== verificationId;

      if (awaitingRun || !isMtlsVerificationSettled(verification)) {
        return MTLS_POLL_INTERVAL_MS;
      }

      return undefined;
    },
    select: data => {
      if (!isMtlsVerificationSettled(data)) {
        return null;
      }

      // Hide the stale previous result while a fresh run is on its way.
      if (verificationId && data?.id !== verificationId) {
        return null;
      }

      return data;
    },
  });
};

export const useUpdateEnvironments = ({
  withCert = true,
}: {
  withCert?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_environments'],
    mutationFn: (payload: DeepPartial<Environment>) => {
      return putEnvironments(payload, { withCert });
    },
    onSuccess: data => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: ['get_environments'],
        });
      }
    },
  });
};
